import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const WellnessContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL + '/api';

const WELLNESS_CONFIG = {
  // Tempo em minutos antes de sugerir pausa
  SUGGESTED_BREAK_INTERVAL: 30,
  // Tempo de descanso sugerido em minutos
  SUGGESTED_BREAK_DURATION: 5,
  // Tempo maximo de uso recomendado por dia (minutos)
  DAILY_LIMIT: 120,
  // Mensagens de bem-estar
  BREAK_MESSAGES: [
    "Ja estas ha {minutes} minutos conectado! Que tal uma pequena pausa?",
    "O teu bem-estar e importante. Considera fazer uma pausa de 5 minutos.",
    "Estiveste muito ativo hoje. Porque nao olhas pela janela um momento?",
    "Lembra-te: as conexoes reais tambem acontecem fora dos ecras.",
    "Uma pausa vai ajudar-te a voltar com mais energia e criatividade.",
    "Estica o teu corpo, respira fundo. A tua mente vai agradecer."
  ],
  BREATHING_EXERCISES: [
    { name: "Respiracao 4-7-8", inhale: 4, hold: 7, exhale: 8 },
    { name: "Respiracao quadrada", inhale: 4, hold: 4, exhale: 4 },
    { name: "Respiracao relaxante", inhale: 5, hold: 2, exhale: 7 }
  ]
};

export const WellnessProvider = ({ children }) => {
  const [sessionStartTime] = useState(Date.now());
  const [totalSessionTime, setTotalSessionTime] = useState(0);
  const [showBreakSuggestion, setShowBreakSuggestion] = useState(false);
  const [breakMessage, setBreakMessage] = useState('');
  const [isOnBreak, setIsOnBreak] = useState(false);
  const [breakTimeRemaining, setBreakTimeRemaining] = useState(0);
  const [dailyUsage, setDailyUsage] = useState(() => {
    const stored = localStorage.getItem('papo_daily_usage');
    if (stored) {
      const { date, minutes } = JSON.parse(stored);
      if (date === new Date().toDateString()) {
        return minutes;
      }
    }
    return 0;
  });
  const [settings, setSettings] = useState(() => {
    const stored = localStorage.getItem('papo_wellness_settings');
    return stored ? JSON.parse(stored) : {
      breakRemindersEnabled: true,
      breakInterval: WELLNESS_CONFIG.SUGGESTED_BREAK_INTERVAL,
      dailyLimitEnabled: true,
      dailyLimit: WELLNESS_CONFIG.DAILY_LIMIT,
      soundEnabled: true
    };
  });
  
  const lastBreakSuggestionRef = useRef(0);
  const breakIntervalRef = useRef(null);
  const dailyUsageRef = useRef(dailyUsage);
  const settingsRef = useRef(settings);

  useEffect(() => { dailyUsageRef.current = dailyUsage; }, [dailyUsage]);
  useEffect(() => { settingsRef.current = settings; }, [settings]);

  // Track session time
  useEffect(() => {
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - sessionStartTime) / 60000);
      setTotalSessionTime(elapsed);
      
      // Update daily usage
      const newDailyUsage = dailyUsageRef.current + 1/60;
      setDailyUsage(newDailyUsage);
      localStorage.setItem('papo_daily_usage', JSON.stringify({
        date: new Date().toDateString(),
        minutes: newDailyUsage
      }));
    }, 60000);

    return () => clearInterval(interval);
  }, [sessionStartTime]);

  // Check for break suggestions
  useEffect(() => {
    if (!settings.breakRemindersEnabled || isOnBreak) return;

    const checkInterval = setInterval(() => {
      const currentSettings = settingsRef.current;
      const sessionMinutes = Math.floor((Date.now() - sessionStartTime) / 60000);
      const timeSinceLastBreak = sessionMinutes - lastBreakSuggestionRef.current;
      
      if (timeSinceLastBreak >= currentSettings.breakInterval && !showBreakSuggestion) {
        const randomMessage = WELLNESS_CONFIG.BREAK_MESSAGES[
          Math.floor(Math.random() * WELLNESS_CONFIG.BREAK_MESSAGES.length)
        ].replace('{minutes}', sessionMinutes.toString());
        
        setBreakMessage(randomMessage);
        setShowBreakSuggestion(true);
        lastBreakSuggestionRef.current = sessionMinutes;
        
        if (currentSettings.soundEnabled) {
          playWellnessSound();
        }
      }
    }, 60000);

    return () => clearInterval(checkInterval);
  }, [sessionStartTime, settings.breakRemindersEnabled, showBreakSuggestion, isOnBreak]);

  // Break timer
  useEffect(() => {
    if (!isOnBreak) return;

    breakIntervalRef.current = setInterval(() => {
      setBreakTimeRemaining(prev => {
        if (prev <= 1) {
          setIsOnBreak(false);
          if (settingsRef.current.soundEnabled) playWellnessSound();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (breakIntervalRef.current) {
        clearInterval(breakIntervalRef.current);
      }
    };
  }, [isOnBreak]);

  const playWellnessSound = () => {
    // Create a gentle notification sound using Web Audio API
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + 0.1);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.8);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.8);
    } catch (e) {
      console.log('Audio not available');
    }
  };

  const dismissBreakSuggestion = useCallback(() => {
    setShowBreakSuggestion(false);
  }, []);

  const startBreak = useCallback((durationMinutes = WELLNESS_CONFIG.SUGGESTED_BREAK_DURATION) => {
    setIsOnBreak(true);
    setBreakTimeRemaining(durationMinutes * 60);
    setShowBreakSuggestion(false);
  }, []);

  const endBreak = useCallback(() => {
    setIsOnBreak(false);
    setBreakTimeRemaining(0);
  }, []);

  const updateSettings = useCallback((newSettings) => {
    const updated = { ...settings, ...newSettings };
    setSettings(updated);
    localStorage.setItem('papo_wellness_settings', JSON.stringify(updated));
  }, [settings]);

  const getRandomBreathingExercise = useCallback(() => {
    return WELLNESS_CONFIG.BREATHING_EXERCISES[
      Math.floor(Math.random() * WELLNESS_CONFIG.BREATHING_EXERCISES.length)
    ];
  }, []);

  // Sync session to backend periodically (every 5 minutes)
  useEffect(() => {
    const syncSession = () => {
      const today = new Date().toISOString().split('T')[0];
      axios.post(`${API}/wellness/session`, {
        date: today,
        minutes: dailyUsageRef.current,
        breaks_taken: 0
      }).catch(() => {});
    };
    const syncInterval = setInterval(syncSession, 300000);
    return () => clearInterval(syncInterval);
  }, []);

  // Sync on page unload too
  useEffect(() => {
    const handleBeforeUnload = () => {
      const today = new Date().toISOString().split('T')[0];
      const blob = new Blob(
        [JSON.stringify({ date: today, minutes: dailyUsageRef.current, breaks_taken: 0 })],
        { type: 'application/json' }
      );
      navigator.sendBeacon?.(`${API}/wellness/session`, blob);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const fetchWeeklyStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/wellness/weekly-stats`);
      return res.data;
    } catch {
      return null;
    }
  }, []);

  const value = {
    // State
    totalSessionTime,
    dailyUsage: Math.round(dailyUsage),
    showBreakSuggestion,
    breakMessage,
    isOnBreak,
    breakTimeRemaining,
    settings,
    
    // Config
    config: WELLNESS_CONFIG,
    
    // Actions
    dismissBreakSuggestion,
    startBreak,
    endBreak,
    updateSettings,
    getRandomBreathingExercise,
    fetchWeeklyStats
  };

  return (
    <WellnessContext.Provider value={value}>
      {children}
    </WellnessContext.Provider>
  );
};

export const useWellness = () => {
  const context = useContext(WellnessContext);
  if (!context) {
    throw new Error('useWellness must be used within a WellnessProvider');
  }
  return context;
};
